import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import { green } from "@mui/material/colors";

import { withTranslation } from "react-i18next";
import { debounce, isEmpty } from "lodash";

import fpItemLeadTimeService from "@plannel/services/fp/fp-item-lead-time-service";
import VersionSearchGroup from "@plannel/pages/factory-plan/common/component/VersionSearchGroup";
import { BUCKET_CASE } from "@plannel/pages/factory-plan/common/component/PeriodSearchGroup";
import BUCKET_CATALOG from "@plannel/pages/factory-plan/common/data/fp-bucket-options.json";
import FpGridSetting, {
  readHide, createSumAwareFormatter, buildGroupColumnPair, REPORT_TREE_GRID_OPTIONS, buildReportStatusBar,
  applyColumnOrder, consumePendingFocus
} from "@plannel/pages/factory-plan/common/grid";
import { buildXAxisSortMap, mergeChartRowsByGroupKey, buildChartDataAndSeries } from "@plannel/pages/factory-plan/common/chart";
import CustomDetailHeader from "@plannel/components/aggrid/CustomDetailHeader";
import { getFilterStore } from "@plannel/components/aggrid/FilterStatusPanel";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import SplitterContainer from "@plannel/components/SplitterContainer";
import PivotChartComposed from "@plannel/components/rechart/PivotChartComposed";
import Snackbar from "@plannel/components/Snackbar";
import { DownloadButton } from "@plannel/components/ActionIconButton";
import ColorUtil from "@plannel/pages/utils/color-util";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";

const { snackBarMessageColor } = ColorUtil;

// old ItemLeadTime.jsx는 PeriodSearchGroup(계획 기간/캘린더 버킷)을 쓰지 않는다 — ltParam/bucketType은
// LT(리드타임) 값 자체의 버킷 단위(시/일/주/월)이고 조회 기간 개념이 아니다(customerleadtime/
// CustomerLeadTime과 동일한 축, fp-report-screen-guide.md §A-3 참고). old가 BUCKET_WITH_HOUR 12종에서
// 'BUCKET_VARIABLE_HOUR' 1개만 걸러냈던 것과 동일하게 제외한다 — 새 백엔드 DayBucket enum(FP 전용,
// Bucket enum과 별개)에 BUCKET_VARIABLE_HOUR가 없어 선택하면 DayBucket.NULL로 빈 응답만 온다.
const BUCKET_OPTIONS = (BUCKET_CATALOG[BUCKET_CASE.WITH_HOUR] ?? [])
  .filter(b => b.value !== "BUCKET_VARIABLE_HOUR");
const DEFAULT_BUCKET_TYPE = (BUCKET_OPTIONS.find(b => b.defaultYn === "Y") ?? BUCKET_OPTIONS[0])?.value ?? "";

// 백엔드가 합계 행에 itemCd='SUM'으로 채워 내려준다(ItemLeadTimeService#getSubSumLtDay) — old
// styleCallback의 'summary-col'/displayCallback의 번역 대응.
const SUM_ROW = "SUM";

// 드릴다운 필터 순서 — old updateChartFromGrid의 column.startsWith('plant'|'itemParentGrp') 분기와
// 동일한 3단계. ItemLeadTimeQueryRepository#makeWhereClause가 그대로 plantCd(단일)/itemParentGrpCd
// (contains)/itemCd(contains)로 필터링한다.
const HIERARCHY_FIELDS = ["plantCd", "itemParentGrpCd", "itemCd"];

// 트리(행 그룹) 계층 — 공장 > 품목상위그룹 > (leaf)품목. old 컬럼정의(plantGroup/itemParentGroup/
// itemGroup 전부 `mergeRule`)를 근거로 Row Grouping을 선택했다(Tree Data 아님, §A-6b). 백엔드가
// 최종적으로 (plantCd+itemParentGrpCd+itemCd) 단위로 집계하므로 품목이 leaf다.
// old 헤더 텍스트 그대로: itemParentGrpCd/itemParentGrpNm은 old에서도 "FP_ITEM_GRP_CD"/"FP_ITEM_GRP_NM"
// 키를 쓴다(고객별 L/T의 itemParentGrpCd와는 다른 화면·다른 레이블 선택 — old 소스 그대로 이관).
const GROUP_FIELDS = ["plantCd", "plantNm", "itemParentGrpCd", "itemParentGrpNm"];

const PLANT_CD_COL_ID = "plantCdGrp";
const PLANT_NM_COL_ID = `PLANT_GRP_NM${GridUtils.IGNORE_P13N_COLLID}`;
const ITEM_PARENT_GRP_CD_COL_ID = "itemParentGrpCdGrp";
const ITEM_PARENT_GRP_NM_COL_ID = `ITEM_PARENT_GRP_GRP_NM${GridUtils.IGNORE_P13N_COLLID}`;
const ITEM_NM_COL_ID = `ITEM_GRP_NM${GridUtils.IGNORE_P13N_COLLID}`;

const PLANT_NM_HIDE_KEY = "plantNmColsHide";
const ITEM_PARENT_GRP_NM_HIDE_KEY = "itemParentGrpNmColsHide";
const ITEM_NM_HIDE_KEY = "itemNmColsHide";

const ItemLeadTime = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);

  const [versionState, setVersionState] = useState({
    planScope: "", planningDate: null, versionCd: "", plantCd: [], selectedVersion: null
  });

  // ltParam/bucketType/isProduct — old defaultValues({ ltParam:0, bucketType:'BUCKET_DAY',
  // isProduct:['true'] })를 그대로 이식한 화면 로컬 state(§A-3c와 동일 원칙, PeriodSearchGroup 미사용).
  const [ltParam, setLtParam] = useState(0);
  const [bucketType, setBucketType] = useState(DEFAULT_BUCKET_TYPE);
  const [isProduct, setIsProduct] = useState(true);

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const gridRef = useRef();
  const [rowData, setRowData] = useState([]);

  const [chartData, setChartData] = useState([]);
  const [chartSeries, setChartSeries] = useState([]);
  const [chartLabels, setChartLabels] = useState([]);

  const latestGridReqRef = useRef(0);
  const latestChartReqRef = useRef(0);
  const prevChartParamsRef = useRef(null);
  const pendingFocusRef = useRef(null);

  // ltParam/bucketType/isProduct는 state지만 debouncedRetrieveRef가 감싸는 콜백이 최초 렌더 시점의
  // 클로저를 재사용하므로(CustomerLeadTime.js와 동일한 이유), 매 렌더 최신값을 ref에 다시 담아
  // retrieveItemLeadTimes가 항상 이 ref를 통해서만 읽게 한다.
  const searchStateRef = useRef({ versionState, ltParam, bucketType, isProduct });
  searchStateRef.current = { versionState, ltParam, bucketType, isProduct };

  const debouncedRetrieveRef = useRef();
  if (!debouncedRetrieveRef.current) {
    debouncedRetrieveRef.current = debounce(() => retrieveItemLeadTimes(), 200);
  }
  useEffect(() => () => debouncedRetrieveRef.current.cancel(), []);

  useEffect(() => () => getFilterStore(viewName).clear(), [viewName]);

  const defaultGridMemo = useMemo(() => {
    const base = FpGridSetting({ title, viewName });
    return {
      ...base,
      defaultColDef: { ...base.defaultColDef, editable: false },
      // 합계 행 강조는 행 전체가 아니라 품목 컬럼부터 피벗 컬럼까지만 cellStyle로 건다
      // (아래 sumRowHighlightStyle) — 배경색만 쓰고 굵게는 안 씀(DP/MP/RP 전반의 green[50] 관례,
      // MaterialPsi 등 지표형 화면과 동일 기준).
      getRowStyle: () => null,
      ...REPORT_TREE_GRID_OPTIONS,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sumAwareFormatter = createSumAwareFormatter(SUM_ROW, t);

  // 합계 행 강조 — 품목(itemCd) 컬럼부터 피벗 컬럼까지 적용(공장/품목상위그룹 컬럼엔 안 건다).
  // 배경색만(green[50]), 굵게는 안 씀.
  const sumRowHighlightStyle = (params) => (
    params.data?.itemCd === SUM_ROW ? { backgroundColor: green[50] } : null
  );

  // 고정(왼쪽 pinned) 컬럼 — old fixColumns(plantCd/plantNm/itemParentGrpCd/itemParentGrpNm/itemCd/
  // itemNm/itemDisplayColor(hidden)/baseLt/planQty/avgLt)와 1:1. 공장/품목상위그룹은 행 그룹(트리),
  // 품목(itemCd)은 leaf라 평범한 field 컬럼 쌍이다.
  const fixedColumnDefs = useMemo(() => [
    ...GROUP_FIELDS.map(field => ({
      headerName: field, field, colId: field, rowGroup: true, hide: true, suppressColumnsToolPanel: true
    })),

    ...buildGroupColumnPair({
      viewName, hidden: readHide(reduxViewState, PLANT_NM_HIDE_KEY), valueFormatter: sumAwareFormatter,
      cdColId: PLANT_CD_COL_ID, cdField: "plantCd", cdHeader: "FP_PLANT_CD",
      nmColId: PLANT_NM_COL_ID, nmField: "plantNm", nmHeader: "PLANT_NM",
      hideKey: PLANT_NM_HIDE_KEY, width: 150
    }),
    ...buildGroupColumnPair({
      viewName, hidden: readHide(reduxViewState, ITEM_PARENT_GRP_NM_HIDE_KEY), valueFormatter: sumAwareFormatter,
      cdColId: ITEM_PARENT_GRP_CD_COL_ID, cdField: "itemParentGrpCd", cdHeader: "FP_ITEM_GRP_CD",
      nmColId: ITEM_PARENT_GRP_NM_COL_ID, nmField: "itemParentGrpNm", nmHeader: "FP_ITEM_GRP_NM",
      hideKey: ITEM_PARENT_GRP_NM_HIDE_KEY, width: 150
    }),

    // leaf(품목) — 그룹이 아니라 실제 행의 값(PlanResult.js/CustomerLeadTime.js의 itemCd와 같은 역할).
    {
      headerName: "FP_ITEM_CD", field: "itemCd", colId: "itemCd", width: 150, pinned: "left", lockPinned: true,
      type: ["nonEditableColumn"], valueFormatter: sumAwareFormatter, cellStyle: sumRowHighlightStyle,
      headerComponent: CustomDetailHeader,
      headerComponentParams: {
        viewName, colsHide: readHide(reduxViewState, ITEM_NM_HIDE_KEY), reduxKey: ITEM_NM_HIDE_KEY, detailColumns: [ITEM_NM_COL_ID]
      }
    },
    {
      headerName: "ITEM_NM", field: "itemNm", colId: ITEM_NM_COL_ID, width: 150, pinned: "left", lockPinned: true,
      hide: readHide(reduxViewState, ITEM_NM_HIDE_KEY), type: ["nonEditableColumn"], valueFormatter: sumAwareFormatter,
      cellStyle: sumRowHighlightStyle
    },

    // old headerText는 공백 1칸 + visible:false — 차트 색상(itemDisplayColor)에 쓰이므로 데이터는 유지.
    { headerName: " ", field: "itemDisplayColor", colId: "itemDisplayColor", hide: true },

    {
      headerName: "FP_BASE_LT_DAY", field: "baseLt", colId: "baseLt", width: 110, pinned: "left", lockPinned: true,
      type: ["numberColumn", "rightAligned"], cellStyle: sumRowHighlightStyle
    },
    {
      headerName: "FP_PLANNED_QTY", field: "planQty", colId: "planQty", width: 100, pinned: "left", lockPinned: true,
      type: ["numberColumn", "rightAligned"], cellStyle: sumRowHighlightStyle
    },
    {
      headerName: "FP_AVERAGE_LT_DAY", field: "avgLt", colId: "avgLt", width: 100, pinned: "left", lockPinned: true,
      type: ["numberColumn", "rightAligned"], cellStyle: sumRowHighlightStyle
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t]);

  // old crosstabHeader는 PlanResult류의 "날짜_주차/월" 2단 그룹이 아니라 LT-bucket 헤더 1개당 컬럼
  // 1개(flat) — 예: "24 H\nD 1"(BUCKET_VARIABLE_DAY, DayBucket#getHeader). old는 헤더에 '\n'이 있으면
  // RealGrid 헤더를 2줄로 그렸지만(styleName:'multi-line-css'), new엔 그런 멀티라인 헤더 스타일이
  // 아직 없어(CustomerLeadTime.js에서 이미 같은 판단) 이 화면 하나만을 위해 새로 만들지 않는다 —
  // '\n'을 공백으로 바꿔 한 줄로 단순화한다(정보 손실 없음, 표시만 한 줄). 동적 문자열이라 i18n
  // 대상이 아니다. 빨간 글씨(cellClassRules)는 old columnInfo styleCallback의
  // `<header>_BASE_LT_FLAG === true` 판정 그대로 — 실제 LT가 기준 LT(baseLt)를 초과한 버킷을 강조한다.
  const buildPivotColumnDefs = (crosstabHeader) => (crosstabHeader ?? []).map(header => ({
    headerName: header.includes("\n") ? header.replace(/\n/g, " ") : header,
    field: header, colId: header, width: 100,
    type: ["numberColumn", "rightAligned"],
    cellStyle: sumRowHighlightStyle,
    cellClassRules: {
      "red-text": (params) => params.data?.[`${header}_BASE_LT_FLAG`] === true
    }
  }));

  // 조회마다 LT-bucket 컬럼 구성 자체가 바뀐다 — GridUtils.setColumnDefs가 되돌리는 "이전 조회의
  // 컬럼 순서"를 crosstabHeader 순서로 다시 못박는다(§A-6c와 동일 이유).
  // old setCrossTabGridData — pivotData[i]/pivotbaseLtData[i]를 crosstabHeader[i] 필드 +
  // "<header>_BASE_LT_FLAG" 컴패니언 필드로 펼친다.
  const buildRowData = (crosstabHeader, data) => (data ?? []).map(row => {
    const flat = {
      plantCd: row.plantCd, plantNm: row.plantNm,
      itemParentGrpCd: row.itemParentGrpCd, itemParentGrpNm: row.itemParentGrpNm,
      itemCd: row.itemCd, itemNm: row.itemNm,
      itemDisplayColor: row.itemDisplayColor,
      baseLt: row.baseLt, planQty: row.planQty, avgLt: row.avgLt
    };
    (crosstabHeader ?? []).forEach((header, index) => {
      flat[header] = row.pivotData?.[index];
      flat[`${header}_BASE_LT_FLAG`] = row.pivotbaseLtData?.[index];
    });
    return flat;
  });

  // 조회/차트 공통 파라미터 — searchStateRef를 통해서만 읽는다(위 선언부 주석 참고, 클로저 staleness
  // 방지). useGrouping은 여기서 넣지 않는다 — old도 그리드 조회(loadData)엔 이 키를 안 보낸다.
  const buildBaseParams = () => {
    const { versionState: version, ltParam: lt, bucketType: bucket, isProduct: product } = searchStateRef.current;
    return {
      viewId: viewName,
      subId: "",
      planScope: version.planScope,
      versionCd: version.versionCd,
      isProduct: !!product,
      ltParam: lt,
      bucketType: bucket
    };
  };

  const clearChart = () => {
    prevChartParamsRef.current = null;
    setChartData([]);
    setChartSeries([]);
    setChartLabels([]);
    getFilterStore(viewName).clear();
  };

  // 하단 상태바 브레드크럼 — FilterStatusPanel의 originLoc/destLoc/item 슬롯에 공장/품목상위그룹/
  // 품목을 얹는다(PlanResult.js의 plant/resource/item 슬롯 매핑과 동일 원리).
  const updateStatusBreadcrumb = (target) => {
    getFilterStore(viewName).setValue({
      originLocCd: target.plantCd ?? null, originLocCdHeader: t("FP_PLANT_CD"),
      originLocNm: target.plantNm ?? null, originLocNmHeader: t("PLANT_NM"),
      destLocCd: target.itemParentGrpCd ?? null, destLocCdHeader: t("FP_ITEM_GRP_CD"),
      destLocNm: target.itemParentGrpNm ?? null, destLocNmHeader: t("FP_ITEM_GRP_NM"),
      itemCd: target.itemCd ?? null, itemCdHeader: t("FP_ITEM_CD"),
      itemNm: target.itemNm ?? null, itemNmHeader: t("ITEM_NM")
    });
  };

  // 클릭 기준으로 차트를 다시 조회한다. target = { plantCd, plantNm, itemParentGrpCd, ... }
  // (상위 레벨을 클릭하면 하위 코드는 없음 — old updateChartFromGrid와 동일). old loadChartData는
  // plantCds(목록) 없이 단일 plantCd만 보낸다 — 그대로 이식(CustomerLeadTime.js와 달리 plantCds를
  // 추가로 보내지 않음, old 소스 기준 충실).
  const loadChart = (target) => {
    const { versionState: version } = searchStateRef.current;
    if (!version.planScope || !version.versionCd) return;
    // SUM(부분합) 행은 차트 갱신 대상에서 제외한다(old의 SUM 셀 클릭 무시와 동일).
    if (target.itemCd === SUM_ROW) return;

    updateStatusBreadcrumb(target);

    const nextParams = {};
    HIERARCHY_FIELDS.forEach(field => { nextParams[field] = target[field] ?? ""; });
    const prev = prevChartParamsRef.current;
    if (prev && HIERARCHY_FIELDS.every(field => prev[field] === nextParams[field])) return;
    prevChartParamsRef.current = nextParams;

    const reqId = ++latestChartReqRef.current;
    // old loadChartData는 'useGrouping':'false'를 항상 명시적으로 보낸다(그리드 조회와 다른 유일한
    // 점 — ItemLeadTimeService는 이 값을 실제로 읽지 않지만 old 소스 그대로 이식).
    const params = { ...buildBaseParams(), ...nextParams, useGrouping: "false" };

    // TODO(MIG-T2): [itemleadtime/ItemLeadTime] 그리드 컬럼 필터(값 목록 체크박스) 미이관.
    //   old: plantCd/itemParentGrpCd/itemCd에 RealGrid autoFilter, onFilteringChanged가 현재
    //        선택 행 기준으로 차트를 다시 그림
    //   사유: 행 그룹(트리)으로 전환하면서 코드 컬럼이 hide:true인 데이터 컬럼이 돼 헤더 필터
    //        아이콘을 띄울 자리가 없어졌다(fp-report-screen-guide.md §A-6e, PlanResult/
    //        CustomerLeadTime과 동일 사유). 백엔드는 changes(FilterParameter)를 이미 읽으므로
    //        2차엔 프론트 트리거 UI만 얹으면 된다 — 지금은 changes 자체를 보내지 않는다.
    fpItemLeadTimeService.getItemLeadTimes(params)
      .then(response => {
        if (reqId !== latestChartReqRef.current) return;

        const { crosstabHeader, data } = response.data ?? {};
        const rows = (data ?? []).filter(r => r.itemCd !== SUM_ROW);

        if (isEmpty(rows) || isEmpty(crosstabHeader)) {
          setChartData([]);
          setChartSeries([]);
          setChartLabels([]);
          return;
        }

        // PivotChartComposed는 groupKey(itemCd) 값으로 범례를 자동 생성하고 그 목록을 data 행과
        // 인덱스로 짝짓는다 — itemCd가 중복되면 값이 어긋난다(PlanResult.js §A-9와 동일 주의).
        // 백엔드가 (plantCd+itemParentGrpCd+itemCd) 단위로 이미 집계해서 내려주지만, 방어적으로
        // 한 번 더 합산한다(common/chart.js — Report 화면 공통). y축 라벨은 old ItemLeadTimeChart의
        // `(${transLangKey('QTY')})`를 그대로 이식(모든 series가 같은 groupName을 가져야 y축이
        // 하나로 합쳐진다).
        const merged = mergeChartRowsByGroupKey({ rows, groupKey: "itemCd", chartHeader: crosstabHeader, displayColorKey: "itemDisplayColor" });
        const { chartData, chartSeries: series } = buildChartDataAndSeries({
          merged, groupKey: "itemCd", chartHeader: crosstabHeader, displayColorKey: "itemDisplayColor",
          seriesOptions: { type: "bar", groupName: `(${t("QTY")})`, stackId: "itemLeadTime" }
        });

        setChartLabels(crosstabHeader);
        setChartData(chartData);
        setChartSeries(series);
      })
      .catch(e => {
        prevChartParamsRef.current = null;
        debug(e);
      });
  };

  const retrieveItemLeadTimes = () => {
    const { versionState: version } = searchStateRef.current;

    if (!version.planScope || !version.versionCd) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("FP_MSG_SELECT_VERSION") });
      return;
    }

    const reqId = ++latestGridReqRef.current;
    const params = { ...buildBaseParams(), plantCds: version.plantCd };

    // 조회 중 AG Grid 내장 로딩 오버레이 표시(§0-8b 공통 컨벤션).
    gridRef.current?.api?.setGridOption("loading", true);

    fpItemLeadTimeService.getItemLeadTimes(params)
      .then(response => {
        if (reqId !== latestGridReqRef.current) return;

        const { crosstabHeader, data } = response.data ?? {};
        const api = gridRef.current?.api;
        if (!api) return;

        const nextColumnDefs = [...fixedColumnDefs, ...buildPivotColumnDefs(crosstabHeader)];
        GridUtils.setColumnDefs({ api, columnDefs: nextColumnDefs, viewName, initState: false });
        applyColumnOrder(api, nextColumnDefs);

        const rows = buildRowData(crosstabHeader, data);
        setRowData(rows);

        if (isEmpty(rows)) {
          clearChart();
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
          return;
        }

        // old: 조회 직후 첫 행의 공장 기준으로 차트를 그리고, 그리드의 현재 선택 셀도 그 행으로
        // 옮긴다(old gridView.setFocus()+setCurrent({itemIndex:1, column:'plantCd'}) 대응 —
        // PlanResult.js/CustomerLeadTime.js와 동일하게 트리의 rowIndex 0인 첫 공장 그룹 행 기준).
        pendingFocusRef.current = { rowIndex: 0, colId: PLANT_CD_COL_ID };
        prevChartParamsRef.current = null;
        loadChart({ plantCd: rows[0].plantCd, plantNm: rows[0].plantNm });
      })
      .catch(e => {
        if (reqId === latestGridReqRef.current) clearChart();
        debug(e);
      })
      .finally(() => {
        if (reqId === latestGridReqRef.current) gridRef.current?.api?.setGridOption("loading", false);
      });
  };

  const onGridReady = (params) => {
    GridUtils.setColumnDefs({ ...params, columnDefs: fixedColumnDefs, viewName, initState: false });
  };

  const onRowDataUpdated = () => consumePendingFocus(gridRef, pendingFocusRef);

  const PLANT_LEVEL_COL_IDS = new Set([PLANT_CD_COL_ID, PLANT_NM_COL_ID]);
  const ITEM_PARENT_GRP_LEVEL_COL_IDS = new Set([ITEM_PARENT_GRP_CD_COL_ID, ITEM_PARENT_GRP_NM_COL_ID]);

  // 클릭한 셀에서 차트를 그릴 드릴다운 대상을 뽑는다(old updateChartFromGrid의
  // `column.startsWith('plant'|'itemParentGrp')` 분기를 트리 표시 컬럼 colId 기준으로 옮긴 것).
  // groupHideOpenParents 때문에 그룹 행(접힌 대표 행)과 leaf(품목) 행 둘 다 다뤄야 한다(§A-6b).
  const resolveChartTarget = (params) => {
    const node = params.node;
    if (!node) return null;

    if (node.group) {
      const target = {};
      for (let current = node; current; current = current.parent) {
        if (current.field) target[current.field] = current.key;
      }
      return isEmpty(target) ? null : target;
    }

    const row = node.data;
    if (!row) return null;

    const colId = params.column?.getColId() ?? "";
    if (PLANT_LEVEL_COL_IDS.has(colId)) {
      return { plantCd: row.plantCd, plantNm: row.plantNm };
    }
    if (ITEM_PARENT_GRP_LEVEL_COL_IDS.has(colId)) {
      return {
        plantCd: row.plantCd, plantNm: row.plantNm,
        itemParentGrpCd: row.itemParentGrpCd, itemParentGrpNm: row.itemParentGrpNm
      };
    }
    return row;
  };

  const onCellClicked = (params) => {
    const target = resolveChartTarget(params);
    if (target) loadChart(target);
  };

  const statusBar = useMemo(() => buildReportStatusBar(viewName), [viewName]);

  // 화면을 아우르는 별도 조회 버튼이 없다(Report 카테고리 전 화면 공통 정책, §A-9b). bucketType
  // (Select)/isProduct(Checkbox)는 선택형이라 이 effect가 즉시 반응하고, ltParam(자유 숫자 입력)은
  // onBlur/Enter로만 수동 커밋한다(아래 TextField 참고, old의 onParamKeyPress와 동일 성격).
  useEffect(() => {
    const version = searchStateRef.current.versionState;
    if (!version.planScope || !version.versionCd) return;

    debouncedRetrieveRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionState.versionCd, versionState.plantCd, bucketType, isProduct]);

  // PlanScope를 바꿔 하위(버전)까지 빈 값으로 리셋된 경우, 그리드·차트에 이전 계획범위의 조회
  // 결과가 그대로 남으면 안 된다 — versionCd가 비면 같이 비운다(PlanResult.js/CustomerLeadTime.js와
  // 동일 원칙).
  useEffect(() => {
    if (versionState.versionCd) return;

    debouncedRetrieveRef.current.cancel();

    const api = gridRef.current?.api;
    if (api) {
      GridUtils.setColumnDefs({ api, columnDefs: fixedColumnDefs, viewName, initState: false });
      applyColumnOrder(api, fixedColumnDefs);
    }
    setRowData([]);
    clearChart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionState.versionCd]);

  const handleLtParamChange = (e) => {
    const raw = e.target.value;
    if (raw === "") {
      setLtParam(0);
      return;
    }
    const parsed = Number(raw);
    if (Number.isNaN(parsed)) return;
    setLtParam(Math.max(0, Math.trunc(parsed)));
  };

  const commitLtParam = () => debouncedRetrieveRef.current();

  // x축(주+월 등 버킷이 섞이면) 기본 사전순 정렬을 chartLabels 원래 순서로 덮어쓴다
  // (common/chart.js, fp-report-screen-guide.md §A-7 — 크로스탭 차트 화면 공통).
  const pivotConfig = useMemo(() => ({
    groupKey: "itemCd",
    chartSeries,
    include: chartLabels,
    xAxisSortMap: buildXAxisSortMap(chartLabels)
  }), [chartSeries, chartLabels]);

  return (
    <>
      <FilterContainer>
        <VersionSearchGroup
          value={versionState}
          onChange={(partial) => setVersionState(prev => ({ ...prev, ...partial }))}
        />
        {/* 자유 숫자 입력 — 선택형 조건과 달리 값이 바뀌는 즉시 재조회하지 않는다(§A-9b, old도
            onParamKeyPress로 Enter일 때만 loadData 호출). 포커스가 빠질 때도 같이 커밋한다. */}
        <TextField
          size="small"
          type="number"
          label={t("FP_LEAD_TIME")}
          value={ltParam}
          onChange={handleLtParamChange}
          onBlur={commitLtParam}
          onKeyDown={(e) => { if (e.key === "Enter") commitLtParam(); }}
          inputProps={{ min: 0 }}
          sx={{ ml: 1.5, minWidth: 120 }}
        />
        <FormControl size="small" sx={{ ml: 1.5, minWidth: 150 }}>
          <InputLabel id="item-lead-time-bucket-label">{t("BUCKET")}</InputLabel>
          <Select
            labelId="item-lead-time-bucket-label"
            label={t("BUCKET")}
            value={bucketType}
            onChange={(e) => setBucketType(e.target.value)}
          >
            {BUCKET_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{t(o.value)}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControlLabel
          sx={{ ml: 1.5 }}
          control={<Checkbox checked={isProduct} onChange={(e) => setIsProduct(e.target.checked)} />}
          label={t("FP_ITEM_CLASS_CD_P")}
        />
        <Box sx={{ display: "flex" }}>
          <DownloadButton
            onClick={() => gridRef.current?.api?.exportDataAsExcel({ fileName: `${t(title)}.xlsx`, sheetName: t(title) })}
          />
        </Box>
      </FilterContainer>
      {/* old VerticalGridChartBox — 그리드 위/차트 아래, 명시적 비율 없이 CSS 기본(50/50에 가까움)
          이었다. SplitterContainer 기본값([3,2]=60/40)을 그대로 두면 그리드가 차트보다 넓어지므로
          PlanResult.js/CustomerLeadTime.js와 동일하게 1:1로 명시한다(§A-1c). */}
      <SplitterContainer initialSizes={[1, 1]}>
        <Box id="fp-item-lead-time-grid" className="ag-theme-balham grid-area" sx={{ height: "calc(100% - 8px)", pb: 1 }}>
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            {...defaultGridMemo}
            statusBar={statusBar}
            onGridReady={onGridReady}
            onRowDataUpdated={onRowDataUpdated}
            onCellClicked={onCellClicked}
          />
        </Box>
        <Box className="chart-area" sx={{ height: "calc(100% - 8px)", mt: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          {/* old ItemLeadTimeChart.jsx는 plugins={[ChartAxisTitlePlugin]}만 걸었다(ChartLegendPlugin
              은 포함되지 않음 — PlanResult와 같은 케이스, CustomerLeadTime/ResourceUtilization과는
              다름, §A-7 "ChartLegendPlugin 판단 주의" 참고). options.plugins.customLegend가 설정돼
              있어도 plugins 배열에 실제로 없으면 렌더되지 않으므로 old에도 범례가 없었다 — recharts
              기본 <Legend/>를 CSS로 숨긴다(공용 파일 수정 없이 old와 동일한 결과). */}
          <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden", ".recharts-legend-wrapper": { display: "none" } }}>
            <PivotChartComposed
              pivotConfig={pivotConfig}
              data={chartData}
              options={{ tooltipContentStyle: { fontSize: "12px" }, minHeight: "0px", decimalPoint: true }}
              showConfigurationPopup={false}
              nullConvert
            />
          </Box>
        </Box>
      </SplitterContainer>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(ItemLeadTime);
